import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Box, Grid, Typography } from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import RMSFileUpload from '../../../shared/components/form-controls/RMSFileUpload/RMSFileUpload';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import AttachmentCameraCapture from './AttachmentCameraCapture';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  ATTACHMENT_DEFAULT_VALUES,
  DICT_SELECT_PROPS,
} from '../../constants/personAttachmentFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { uploadAttachment, editAttachment } from '../../../shared/services/attachmentService';
import { buildPersonAttachmentSchema } from '../../schema/personAttachmentSchema';
import { formatFileSize, splitFileName } from '../../utility/attachmentHelper';

const PersonAttachmentDialog = ({ open, onClose, personId, attachment = null, onSaved }) => {
  const dispatch = useDispatch();
  const sizeLimit = useSelector((state) => state.attachments.sizelimit);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const isEditMode = Boolean(attachment?.ptsAttachmentID);
  const { attachmentTypeOptions } = useUserDictionaryOptions();

  const methods = useForm({
    resolver: CreateFormResolver(buildPersonAttachmentSchema(isEditMode)),
    mode: 'onSubmit',
    defaultValues: isEditMode
      ? {
          FileName: splitFileName(attachment.FileName, attachment.FileType),
          Description: attachment.Description || '',
          codeAttachmentType: attachment.codeAttachmentType || '',
          IsDeleted: Boolean(attachment.IsDeleted),
        }
      : ATTACHMENT_DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const file = useMemo(() => pendingFiles[0]?.file ?? null, [pendingFiles]);

  const handleCameraCapture = useCallback(
    (capturedFile) => {
      if (capturedFile.size > sizeLimit * 1024 * 1024) {
        dispatch(
          notify(`File size limit exceeded. Maximum allowed per file: ${sizeLimit} MB.`, 'error')
        );
        return;
      }

      setPendingFiles([
        {
          id: `${capturedFile.name}_${capturedFile.size}_${capturedFile.lastModified}`,
          file: capturedFile,
          title: capturedFile.name.replace(/\.[^/.]+$/, ''),
        },
      ]);
    },
    [dispatch, sizeLimit]
  );

  const handleRemoveFile = useCallback(() => setPendingFiles([]), []);

  const onSubmit = useCallback(
    async (data) => {
      if (isEditMode) {
        const payload = {
          ptsAttachmentID: attachment.ptsAttachmentID,
          FileName: `${data.FileName}.${attachment.FileType}`,
          Description: data.Description,
          codeAttachmentType: data.codeAttachmentType,
          IsDeleted: data.IsDeleted,
        };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: () => editAttachment(attachment.ptsAttachmentID, payload),
          onSuccess: () => {
            dispatch(notify('Attachment updated successfully', 'success'));
            onSaved?.();
          },
        });

        return;
      }

      const formData = new FormData();

      formData.append('parentType', 'PERSON');
      formData.append('ptsParentID', personId);
      formData.append('description', data.Description || '');
      formData.append('codeAttachmentType', data.codeAttachmentType || '');
      formData.append('fileName', file.name);
      formData.append('fileType', file.name.split('.').pop());
      formData.append('mimeType', file.type);
      formData.append('size', file.size);
      formData.append('file', file);

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => uploadAttachment(formData),
        onSuccess: () => {
          dispatch(notify('Attachment uploaded successfully', 'success'));
          onSaved?.();
        },
      });
    },
    [isEditMode, attachment, personId, file, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }
    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Attachment' : 'Add Attachment'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting || (!isEditMode && !file)}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!attachment?.Created}
              created={attachment?.Created}
              createdBy={attachment?.CreatedBy}
              updated={attachment?.Updated}
              updatedBy={attachment?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            {isEditMode ? (
              <Grid item xs={12}>
                <RMSTextField name="FileName" label="File Name" inputProps={{ maxLength: 255 }} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <RMSFileUpload
                    files={pendingFiles}
                    onChange={setPendingFiles}
                    multiple={false}
                    maxFiles={1}
                    maxFileSize={sizeLimit}
                    accept="*"
                    subtitle={`Any file type is supported — up to ${sizeLimit} MB`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AttachmentCameraCapture onCapture={handleCameraCapture} />
                </Grid>
                {file && (
                  <Grid item xs={12}>
                    <Box className="person-attachment-selected-file">
                      <AttachFileIcon fontSize="small" />
                      <Typography variant="body2" className="person-attachment-selected-file__name">
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({formatFileSize(file.size)})
                      </Typography>
                      <RMSIconButton
                        icon={<CloseIcon fontSize="small" />}
                        tooltip="Remove"
                        onClick={handleRemoveFile}
                      />
                    </Box>
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12}>
              <RMSSelect
                name="codeAttachmentType"
                label="Type"
                options={attachmentTypeOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <RMSTextField
                name="Description"
                label="Description"
                inputProps={{ maxLength: isEditMode ? 50 : 100 }}
              />
            </Grid>
            {isEditMode && (
              <Grid item xs={12}>
                <RMSCheckbox name="IsDeleted" label="Is Deleted" />
              </Grid>
            )}
          </Grid>

          {!isEditMode && !file && (
            <Typography variant="caption" color="textSecondary">
              Select a file above before saving.
            </Typography>
          )}
        </FormProvider>
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default PersonAttachmentDialog;
